// Shared helpers for opportunity display
export const ASSET_TYPES = [
  { value: "business", label: "Business" },
  { value: "multifamily", label: "Multifamily" },
  { value: "land", label: "Land" },
];

export const PIPELINE_STAGES = [
  { value: "new_lead", label: "New Lead" },
  { value: "contact_attempted", label: "Contact Attempted" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "negotiating", label: "Negotiating" },
  { value: "loi_sent", label: "LOI Sent" },
  { value: "under_contract", label: "Under Contract" },
  { value: "due_diligence", label: "Due Diligence" },
  { value: "closed", label: "Closed" },
  { value: "lost", label: "Lost" },
];

// Acquisition Pipeline (V2) — the 10-stage automated funnel used by the
// Pipeline Kanban (/app/pipeline). The Opportunity Detail page surfaces the
// same stage list so both views stay in sync.
//   - GATED stages require a paid tier to advance INTO
//   - MANUAL stages must be moved by the subscriber (no auto-advance)
//   - AUTO  stages move automatically based on signals/score/events
export const CF_STAGES = [
  { value: "discovered",      label: "Discovered",      mode: "auto"   },
  { value: "scored",          label: "Scored",          mode: "auto"   },
  { value: "outreach_sent",   label: "Outreach Sent",   mode: "auto",   gated: true },
  { value: "engaged",         label: "Engaged",         mode: "auto",   gated: true },
  { value: "offer_stage",     label: "Offer Stage",     mode: "manual", gated: true },
  { value: "under_loi",       label: "Under LOI",       mode: "auto",   gated: true },
  { value: "due_diligence",   label: "Due Diligence",   mode: "manual", gated: true },
  { value: "contract_signed", label: "Contract Signed", mode: "auto",   gated: true },
  { value: "closed",          label: "Closed",          mode: "manual", gated: true },
  { value: "dead",            label: "Dead",            mode: "manual" },
];

export const cfStageLabel = (s) =>
  CF_STAGES.find((x) => x.value === s)?.label || s || "—";

export const cfStageColor = (s) => {
  const map = {
    discovered:      "bg-slate-500/15 text-slate-300 border-slate-500/30",
    scored:          "bg-sky-500/15 text-sky-300 border-sky-500/30",
    outreach_sent:   "bg-blue-500/15 text-blue-300 border-blue-500/30",
    engaged:         "bg-amber-500/15 text-amber-300 border-amber-500/30",
    offer_stage:     "bg-orange-500/15 text-orange-300 border-orange-500/30",
    under_loi:       "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    due_diligence:   "bg-violet-500/15 text-violet-300 border-violet-500/30",
    contract_signed: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    closed:          "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    dead:            "bg-rose-500/15 text-rose-300 border-rose-500/30",
  };
  return map[s] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
};

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY"
];

export const EMPTY = "N/A";

export const fmtCurrency = (n) => {
  if (n == null || n === 0) return EMPTY;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

export const fmtCurrencyFull = (n) => {
  if (n == null) return EMPTY;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const fmtPercent = (n) => (n == null ? EMPTY : `${n}%`);

export const scoreColor = (s) => {
  if (s >= 80) return "text-emerald-400";
  if (s >= 60) return "text-amber-400";
  if (s >= 40) return "text-orange-400";
  return "text-rose-400";
};

export const scoreBg = (s) => {
  if (s >= 80) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (s >= 60) return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (s >= 40) return "bg-orange-500/15 text-orange-300 border-orange-500/30";
  return "bg-rose-500/15 text-rose-300 border-rose-500/30";
};

export const stageColor = (s) => {
  const map = {
    new_lead: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    contact_attempted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    contacted: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
    qualified: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    negotiating: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    loi_sent: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    under_contract: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    due_diligence: "bg-teal-500/15 text-teal-300 border-teal-500/30",
    closed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    lost: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  };
  return map[s] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
};

export const stageLabel = (s) =>
  PIPELINE_STAGES.find((x) => x.value === s)?.label || s;

export const assetIcon = (t) => {
  // Returns a lucide icon name string consumed by AssetIcon component
  if (t === "business") return "Building2";
  if (t === "multifamily") return "Building";
  if (t === "land") return "Mountain";
  return "Briefcase";
};


/**
 * BLOCK 58 — clean address formatter.
 *
 * Rendering an opportunity's address by simple template literal like
 *   `${address}, ${city}, ${state} ${zip}`
 * produces artefacts like "12345 Main St, Main City, GA , ,  " when the
 * underlying row has the full address embedded in `address` and empty
 * `city`/`state`/`zip_code`. The data itself is fine — every field is
 * either a non-empty string or falsy — the join needs to skip empties.
 *
 * Returns a cleanly-joined single-line address string, or an empty string
 * if nothing is present. Also collapses any accidental multi-comma
 * sequences that may already exist inside a field value (e.g. legacy
 * migrated rows where the whole address was stuffed into `.address`
 * with a trailing comma).
 */
export function formatFullAddress({ address, city, state, zip_code, zip } = {}) {
  const zipVal = zip_code || zip || "";
  const cityStateZip = [
    (city || "").trim(),
    [(state || "").trim(), String(zipVal || "").trim()].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");
  const parts = [
    (address || "").trim().replace(/,\s*$/, ""), // strip trailing comma on address
    cityStateZip,
  ].filter(Boolean);
  return parts
    .join(", ")
    // collapse any duplicate commas that snuck in from mixed data
    .replace(/,\s*,+/g, ",")
    .replace(/\s{2,}/g, " ")
    .replace(/,\s*$/, "")
    .trim();
}

/** Compact "City, ST" line for list views. Skips empty fields. */
export function formatCityState({ city, state } = {}) {
  return [(city || "").trim(), (state || "").trim()].filter(Boolean).join(", ");
}
